import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Button,
    Paper,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Card,
    CardContent,
    Collapse,
    IconButton,
    InputAdornment,
    Pagination,
    Stack
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import productService from '../services/productService';
import ProductCard from './ProductCard';
import '../styles/ProtectedRoutes.css';

const ProductPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalProducts, setTotalProducts] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);

    const ITEMS_PER_PAGE = 12;

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        category: 'Ready Made', // Default to Ready Made products
        productType: '',
        color: '',
        minPrice: '',
        maxPrice: '',
        sortBy: 'CreatedAt',
        sortOrder: 'DESC'
    });

    // Meta data for filters
    const [productTypes, setProductTypes] = useState([]);
    const [availableColors, setAvailableColors] = useState([]);

    const loadProducts = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * ITEMS_PER_PAGE;

            const response = await productService.getCustomerProducts(filters, ITEMS_PER_PAGE, offset);

            if (response.success) {
                const tagPriority = { 'Bestseller': 1, 'Trending': 2, 'Featured': 3, 'Limited Edition': 4 };
                const sorted = [...response.data].sort((a, b) => {
                    const aPriority = tagPriority[a.ProductTag] || 5;
                    const bPriority = tagPriority[b.ProductTag] || 5;
                    return aPriority - bPriority;
                });
                setProducts(sorted);
                setTotalProducts(response.pagination.total || 0);
            }

            if (response.success) {
                setProducts(response.data);
                setTotalProducts(response.pagination.total || 0);
            } else {
                toast.error('Failed to load products');
            }
        } catch (error) {
            console.error('Load products error:', error);
            toast.error(error.message || 'Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const loadFilterOptions = async () => {
        try {
            const [typesResponse, colorsResponse] = await Promise.all([
                productService.getProductTypes(),
                productService.getAvailableColors()
            ]);

            if (typesResponse.success) {
                setProductTypes(typesResponse.data);
            }

            if (colorsResponse.success) {
                setAvailableColors(colorsResponse.data);
            }
        } catch (error) {
            console.error('Load filter options error:', error);
        }
    };

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentPage(1); // Reset to first page when filters change
            loadProducts(1);
        }, 300); // Debounce search

        return () => clearTimeout(timeoutId);
    }, [filters.search, filters.category, filters.productType, filters.color, filters.minPrice, filters.maxPrice, filters.sortBy, filters.sortOrder]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            category: 'Ready Made', // Keep Ready Made as default
            productType: '',
            color: '',
            minPrice: '',
            maxPrice: '',
            sortBy: 'CreatedAt',
            sortOrder: 'DESC'
        });
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        loadProducts(page);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

    const hasActiveFilters = Object.entries(filters).some(([key, value]) =>
        key !== 'sortBy' && key !== 'sortOrder' && value !== ''
    );

    if (loading && products.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading products...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
            {/* Header */}
            
                <Typography variant="h4" gutterBottom className="glass-title">
                    Ready Made Products
                </Typography>
                <Typography variant="body1" className="glass-text-secondary" sx={{ mb: 3 }}>
                    Discover our beautiful collection of ready-made fashion pieces
                </Typography>
            

            {/* Search and Filter Bar */}
            <Paper elevation={1} className="glass-content-container" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search products..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                            flexGrow: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '& fieldset': {
                                    borderColor: 'rgba(188, 150, 94, 0.3)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(188, 150, 94, 0.5)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#bc965e',
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: '#ffffff',
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                opacity: 1,
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#bc965e' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        variant={showFilters ? "contained" : "outlined"}
                        startIcon={<FilterListIcon />}
                        onClick={() => setShowFilters(!showFilters)}
                        className={showFilters ? "glass-button" : "glass-button-outlined"}
                        sx={{ position: 'relative' }}
                    >
                        Filters
                        {hasActiveFilters && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 12,
                                    height: 12,
                                    bgcolor: 'error.main',
                                    borderRadius: '50%'
                                }}
                            />
                        )}
                    </Button>
                </Box>
            </Paper>

            {/* Filter Panel */}
            <Collapse in={showFilters}>
                <Card elevation={2} className="glass-content-container" sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" className="glass-title">Filters</Typography>
                            <IconButton onClick={() => setShowFilters(false)} size="small" className="glass-icon-button">
                                <CloseIcon sx={{ color: '#bc965e' }} />
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                            }}
                        >
                            {/* Product Type */}
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 11px)' } }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Product Type</InputLabel>
                                    <Select
                                        value={filters.productType}
                                        label="Product Type"
                                        onChange={(e) => handleFilterChange('productType', e.target.value)}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.3)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.5)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#bc965e',
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#ffffff',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#bc965e',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                className: 'glass-menu'
                                            }
                                        }}
                                    >
                                        <MenuItem value="" className="glass-menu-item">All Types</MenuItem>
                                        {productTypes.map((type, index) => (
                                            <MenuItem key={`type-${type.ProductTypeId || type.TypeName}-${index}`} value={type.TypeName} className="glass-menu-item">
                                                {type.TypeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Color */}
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 11px)' } }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Color</InputLabel>
                                    <Select
                                        value={filters.color}
                                        label="Color"
                                        onChange={(e) => handleFilterChange('color', e.target.value)}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.3)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.5)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#bc965e',
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#ffffff',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#bc965e',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                className: 'glass-menu'
                                            }
                                        }}
                                    >
                                        <MenuItem value="" className="glass-menu-item">All Colors</MenuItem>
                                        {availableColors.map((color, index) => (
                                            <MenuItem key={`color-${color}-${index}`} value={color} className="glass-menu-item">
                                                {color}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Min/Max Price */}
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 11px)' } }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        type="number"
                                        placeholder="Min Price"
                                        value={filters.minPrice}
                                        onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                                        size="small"
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                '& fieldset': {
                                                    borderColor: 'rgba(188, 150, 94, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(188, 150, 94, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#bc965e',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#ffffff',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                    <TextField
                                        type="number"
                                        placeholder="Max Price"
                                        value={filters.maxPrice}
                                        onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                                        size="small"
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                '& fieldset': {
                                                    borderColor: 'rgba(188, 150, 94, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(188, 150, 94, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#bc965e',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#ffffff',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Sort By */}
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 11px)' } }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Sort By</InputLabel>
                                    <Select
                                        value={filters.sortBy}
                                        label="Sort By"
                                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.3)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.5)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#bc965e',
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#ffffff',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#bc965e',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                className: 'glass-menu'
                                            }
                                        }}
                                    >
                                        <MenuItem value="CreatedAt" className="glass-menu-item">Newest First</MenuItem>
                                        <MenuItem value="ProductName" className="glass-menu-item">Name</MenuItem>
                                        <MenuItem value="Price" className="glass-menu-item">Price</MenuItem>
                                        <MenuItem value="Category" className="glass-menu-item">Category</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Order */}
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 11px)' } }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Order</InputLabel>
                                    <Select
                                        value={filters.sortOrder}
                                        label="Order"
                                        onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.3)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(188, 150, 94, 0.5)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#bc965e',
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#ffffff',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#bc965e',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                className: 'glass-menu'
                                            }
                                        }}
                                    >
                                        <MenuItem value="ASC" className="glass-menu-item">Ascending</MenuItem>
                                        <MenuItem value="DESC" className="glass-menu-item">Descending</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {hasActiveFilters && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={clearFilters}
                                    className="glass-button-outlined"
                                >
                                    Clear All Filters
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Collapse>

            {/* Products Grid */}

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        maxWidth: '1400px',
                        justifyContent: 'center',
                    }}
                >
                    {products.map((product, index) => (
                        <Box
                            key={`product-${product.ProductCode || 'unknown'}-${index}`}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                flex: {
                                    xs: '1 1 100%',
                                    sm: '1 1 calc(50% - 12px)',
                                    md: '1 1 calc(33.33% - 16px)',
                                    lg: '1 1 calc(25% - 18px)',
                                    xl: '1 1 calc(20% - 19.2px)',
                                },
                            }}
                        >
                            <ProductCard product={product} />
                        </Box>
                    ))}
                </Box>
            </Box>


            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Stack spacing={2}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#ffffff',
                                    borderColor: 'rgba(188, 150, 94, 0.3)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(188, 150, 94, 0.1)',
                                        borderColor: 'rgba(188, 150, 94, 0.5)',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#bc965e',
                                        color: '#000000',
                                        '&:hover': {
                                            backgroundColor: '#d4a96a',
                                        },
                                    },
                                },
                            }}
                        />
                        <Typography variant="body2" className="glass-text-secondary" textAlign="center">
                            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalProducts)} of {totalProducts} products
                        </Typography>
                    </Stack>
                </Box>
            )}

            {/* Empty State */}
            {products.length === 0 && !loading && (
                <Paper elevation={1} className="glass-empty-state">
                    <Box sx={{ mb: 2, fontSize: '4rem' }}>👗</Box>
                    <Typography variant="h6" gutterBottom className="glass-text-primary">
                        No products found
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        {hasActiveFilters
                            ? 'Try adjusting your filters to see more products.'
                            : 'We\'re working on adding new products. Check back soon!'
                        }
                    </Typography>
                </Paper>
            )}
        </Container>
    );
};

export default ProductPage;